/**
 * Program Scheduling Types
 * ========================
 * Extended types for the calendar-based program builder.
 * These extend the base database types with scheduling capabilities.
 */

import type { DifficultyLevel, VideoVisibility, Video, ProgramItem, Program } from './database.types'

/**
 * Program organization type - how tasks are scheduled
 */
export type ProgramOrganizationType = 'day_based' | 'week_based' | 'calendar_based'

/**
 * Program type - individual vs group
 */
export type ProgramType = 'individual' | 'group'

/**
 * Duration preset options (legacy - complex scheduling)
 */
export type DurationPreset = '2_weeks' | '4_weeks' | '3_months' | 'season' | 'custom'

/**
 * Simplified duration preset options (new wizard flow)
 * Can be a built-in preset ID or a custom duration ID (custom_xxx)
 */
export type SimpleDurationPreset = '2_weeks' | '4_weeks' | '3_months' | '6_months' | '1_year' | string

/**
 * Duration option structure (used for both default and custom durations)
 */
export interface DurationOption {
  id: string           // Unique identifier (e.g., '2_weeks', 'custom_abc123')
  label: string        // Display label (e.g., '2 Weeks')
  weeks: number        // Duration in weeks
  description: string  // Helper text
  isDefault: boolean   // True for built-in options, false for custom
  isHidden?: boolean   // Allow users to hide default options
}

/**
 * User's duration preset preferences (stored in localStorage)
 */
export interface DurationPresetSettings {
  hiddenDefaults: string[]      // IDs of default options user has hidden
  customDurations: DurationOption[]  // User-created custom durations
  order?: string[]              // Custom ordering of all duration IDs (future)
}

/**
 * Duration to weeks mapping helper (for backwards compatibility)
 */
export const DURATION_TO_WEEKS: Record<string, number> = {
  '2_weeks': 2,
  '4_weeks': 4,
  '3_months': 12,
  '6_months': 26,
  '1_year': 52,
}

/**
 * Default duration options (built-in)
 */
export const DEFAULT_DURATION_OPTIONS: DurationOption[] = [
  { id: '2_weeks', label: '2 Weeks', weeks: 2, description: 'Quick intro or rehab program', isDefault: true },
  { id: '4_weeks', label: '4 Weeks', weeks: 4, description: 'Standard training block', isDefault: true },
  { id: '3_months', label: '3 Months', weeks: 12, description: 'Full training cycle', isDefault: true },
  { id: '6_months', label: '6 Months', weeks: 26, description: 'Extended program', isDefault: true },
  { id: '1_year', label: '1 Year', weeks: 52, description: 'Long-term development', isDefault: true },
]

/**
 * Legacy duration options array (for backwards compatibility)
 */
export const SIMPLE_DURATION_OPTIONS: { value: SimpleDurationPreset; label: string; description: string }[] = [
  { value: '2_weeks', label: '2 Weeks', description: 'Quick intro or rehab program' },
  { value: '4_weeks', label: '4 Weeks', description: 'Standard training block' },
  { value: '3_months', label: '3 Months', description: 'Full training cycle' },
  { value: '6_months', label: '6 Months', description: 'Extended program' },
  { value: '1_year', label: '1 Year', description: 'Long-term development' },
]

/**
 * Default preset settings (no customizations)
 */
export const DEFAULT_DURATION_PRESET_SETTINGS: DurationPresetSettings = {
  hiddenDefaults: [],
  customDurations: [],
}

/**
 * Helper to get weeks from a duration ID (works with both default and custom)
 */
export function getWeeksFromDurationId(durationId: string, customDurations: DurationOption[] = []): number {
  // Check built-in durations first
  if (DURATION_TO_WEEKS[durationId]) {
    return DURATION_TO_WEEKS[durationId]
  }
  // Check custom durations
  const custom = customDurations.find(d => d.id === durationId)
  if (custom) {
    return custom.weeks
  }
  // Default fallback
  return 4
}

/**
 * Helper to get duration ID from weeks (reverse lookup for edit mode)
 * Returns the first matching duration ID, prioritizing default options
 */
export function getDurationIdFromWeeks(weeks: number | null, customDurations: DurationOption[] = []): SimpleDurationPreset {
  if (!weeks) return '4_weeks' // default

  // Check default durations first
  for (const [id, w] of Object.entries(DURATION_TO_WEEKS)) {
    if (w === weeks) return id as SimpleDurationPreset
  }

  // Check custom durations
  const custom = customDurations.find(d => d.weeks === weeks)
  if (custom) return custom.id

  // No exact match - find closest default or return default
  return '4_weeks'
}

/**
 * Program basics form data (Step 1 - legacy)
 */
export interface ProgramBasicsData {
  title: string
  description: string
  programType: ProgramType
  visibility: VideoVisibility
  difficultyLevel: DifficultyLevel | null
  isTemplate: boolean
}

/**
 * Program basics form data with duration (Step 1 - new wizard)
 */
export interface ProgramBasicsDataV2 {
  title: string
  description: string
  programType: ProgramType
  visibility: VideoVisibility
  difficultyLevel: DifficultyLevel | null
  isTemplate: boolean
  duration: SimpleDurationPreset
}

/**
 * Assignment mode options
 */
export type AssignmentMode = 'skip' | 'individual' | 'multiple' | 'group'

/**
 * Assignment step data (Step 3 - new wizard)
 */
export interface AssignmentStepData {
  mode: AssignmentMode
  clientId?: string
  selectedClientIds: Set<string>
  groupId?: string
  notes: string
}

/**
 * Duration configuration for programs
 */
export interface ProgramDurationConfig {
  preset: DurationPreset
  startDate?: Date
  endDate?: Date
  accessMonths: number
}

/**
 * Schedule structure form data (Step 2)
 */
export interface ScheduleStructureData {
  organizationType: ProgramOrganizationType
  duration: ProgramDurationConfig
  daysPerWeek?: number[]  // 0-6 for weekly patterns
}

/**
 * Task/exercise data with scheduling
 */
export interface ProgramTaskData {
  id?: string
  exerciseName: string
  notes?: string
  videoId: string
  video?: Pick<Video, 'id' | 'title' | 'thumbnail_url' | 'duration_seconds' | 'mux_playback_id' | 'thumbnail_time'>

  // Scheduling (one of these based on organization type)
  scheduledDay?: number      // Day 1, 2, 3... (for day_based)
  scheduledWeek?: number     // Week 1, 2, 3... (for week_based)
  scheduledDate?: Date       // Specific date (for calendar_based)
  scheduledTime?: string     // HH:MM format

  // Exercise details
  sets?: number
  reps?: number
  durationSeconds?: number
  restSeconds?: number

  // Recurrence (RRULE format)
  recurrenceRule?: string
}

/**
 * Wizard step definition
 */
export interface WizardStep {
  id: string
  title: string
  description: string
  isCompleted: boolean
  isActive: boolean
}

/**
 * Complete program wizard state
 */
export interface ProgramWizardState {
  currentStep: number
  basics: ProgramBasicsData
  schedule: ScheduleStructureData
  tasks: ProgramTaskData[]
  isValid: boolean
  isDirty: boolean
}

/**
 * Extended program with scheduling fields
 * (Used until database types are regenerated)
 */
export interface ProgramWithScheduling extends Program {
  program_type: ProgramType | null
  organization_type: ProgramOrganizationType | null
  start_date: string | null
  end_date: string | null
}

/**
 * Extended program item with scheduling fields
 */
export interface ProgramItemWithScheduling extends ProgramItem {
  scheduled_day: number | null
  scheduled_week: number | null
  scheduled_time: string | null
}

/**
 * Calendar event for FullCalendar
 */
export interface CalendarEvent {
  id: string
  title: string
  start: Date
  end?: Date
  allDay?: boolean
  extendedProps: {
    taskData: ProgramTaskData
    isRecurring?: boolean
  }
}

/**
 * Day slot for day-based programs
 */
export interface DaySlot {
  dayNumber: number
  label: string
  tasks: ProgramTaskData[]
}

/**
 * Week slot for week-based programs
 */
export interface WeekSlot {
  weekNumber: number
  label: string
  days: DaySlot[]
}

/**
 * Default values for new programs
 */
export const DEFAULT_PROGRAM_BASICS: ProgramBasicsData = {
  title: '',
  description: '',
  programType: 'individual',
  visibility: 'private',
  difficultyLevel: null,
  isTemplate: false,
}

export const DEFAULT_SCHEDULE_STRUCTURE: ScheduleStructureData = {
  organizationType: 'day_based',
  duration: {
    preset: '4_weeks',
    accessMonths: 3,
  },
  daysPerWeek: [1, 3, 5], // Mon, Wed, Fri by default
}

export const DEFAULT_WIZARD_STATE: ProgramWizardState = {
  currentStep: 0,
  basics: DEFAULT_PROGRAM_BASICS,
  schedule: DEFAULT_SCHEDULE_STRUCTURE,
  tasks: [],
  isValid: false,
  isDirty: false,
}

// ============================================
// NEW WIZARD FLOW (V2) - Simplified 3-step
// ============================================

/**
 * New wizard state (V2) - Simplified 3-step flow
 * Step 1: Basics (with duration)
 * Step 2: Exercises (with upload)
 * Step 3: Assign Clients (optional)
 */
export interface ProgramWizardStateV2 {
  currentStep: number
  basics: ProgramBasicsDataV2
  tasks: ProgramTaskData[]
  assignment: AssignmentStepData
  isValid: boolean
  isDirty: boolean
  createdProgramId?: string  // Set after program is created, before assignment
}

/**
 * Default values for new wizard (V2)
 */
export const DEFAULT_PROGRAM_BASICS_V2: ProgramBasicsDataV2 = {
  title: '',
  description: '',
  programType: 'individual',
  visibility: 'private',
  difficultyLevel: null,
  isTemplate: false,
  duration: '4_weeks',
}

export const DEFAULT_ASSIGNMENT_STEP: AssignmentStepData = {
  mode: 'skip',
  clientId: undefined,
  selectedClientIds: new Set(),
  groupId: undefined,
  notes: '',
}

export const DEFAULT_WIZARD_STATE_V2: ProgramWizardStateV2 = {
  currentStep: 0,
  basics: DEFAULT_PROGRAM_BASICS_V2,
  tasks: [],
  assignment: DEFAULT_ASSIGNMENT_STEP,
  isValid: false,
  isDirty: false,
}
